// SCR-STAFF-905 — ورود داده (`/settings/import`؛ US-PLT-060 تا 063؛ AP-PLT-03): زبانه «ورود تازه» (جادوی ۵ مرحله‌ای) و «تاریخچه»
// (کارهای ورود با وضعیت، ادامه، فایل خطا، بازگشت با توضیح وابستگی‌ها و حذف کار اجرا نشده).
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type ImportJobView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, DataTable, faDigits, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorsDownloadButton, ImportWizard } from './ImportWizard';
import { RollbackAction } from './RollbackAction';
import { EXECUTED_STATUSES, ImportStatusChip, importKeys, isBusy, SCR_STAFF_905 } from './common';

export function ImportPage() {
  const { t } = useTranslation('plt');
  const [tab, setTab] = useState<'wizard' | 'history'>('wizard');
  const [jobId, setJobId] = useState<string | null>(null);
  const [wizardKey, setWizardKey] = useState(0);
  const write = useWriteAccess('plt.import.run');

  return (
    <ListPage screenId={SCR_STAFF_905} titleKey="plt:import.title" subtitleKey="plt:import.subtitle">
      <Tabs value={tab} onChange={(_event, value: 'wizard' | 'history') => setTab(value)} sx={{ mb: 2 }} aria-label={t('plt:import.title')}>
        <Tab value="wizard" label={t('plt:import.tabs.wizard')} data-testid="import-tab-wizard" />
        <Tab value="history" label={t('plt:import.tabs.history')} data-testid="import-tab-history" />
      </Tabs>
      {!write.canWrite && write.blockedByAccessMode && (
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          {t('plt:write_notice.access_mode')}
        </Typography>
      )}
      {tab === 'wizard' ? (
        <ImportWizard key={wizardKey} jobId={jobId} onJobChange={setJobId} onShowHistory={() => setTab('history')} />
      ) : (
        <ImportHistory
          onOpen={(job) => {
            setJobId(job.id);
            setWizardKey((key) => key + 1);
            setTab('wizard');
          }}
        />
      )}
    </ListPage>
  );
}

function ImportHistory({ onOpen }: { onOpen: (job: ImportJobView) => void }) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.import.run');
  const [deleting, setDeleting] = useState<ImportJobView | null>(null);

  const list = useInfiniteQuery({
    queryKey: importKeys.list(session?.tenantId),
    queryFn: ({ pageParam }) =>
      unwrap(tenantApi.GET('/api/v1/platform/imports', { params: { query: { cursor: pageParam ?? undefined, limit: 20 } } })),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    refetchInterval: (query) => (query.state.data?.pages.some((page) => page.items.some((job) => isBusy(job))) ? 2000 : false),
  });

  const remove = useMutation({
    mutationFn: (job: ImportJobView) =>
      unwrap(tenantApi.DELETE('/api/v1/platform/imports/{id}', { params: { path: { id: job.id } } })),
    onSuccess: () => {
      setDeleting(null);
      void queryClient.invalidateQueries({ queryKey: importKeys.all(session?.tenantId) });
    },
  });

  const columns: DataTableColumn<ImportJobView>[] = [
    { id: 'type', headerKey: 'plt:import.history.columns.type', cell: (row) => t(`plt:import.types.${row.type}`) },
    {
      id: 'file',
      headerKey: 'plt:import.history.columns.file',
      cell: (row) => (
        <Stack>
          <bdi>{row.fileName}</bdi>
          {!row.fileAvailable && (
            <Typography variant="caption" color="text.secondary">
              {t('plt:import.history.file_deleted')}
            </Typography>
          )}
        </Stack>
      ),
    },
    { id: 'status', headerKey: 'plt:import.history.columns.status', cell: (row) => <ImportStatusChip status={row.status} /> },
    { id: 'rows', headerKey: 'plt:import.history.columns.rows', cell: (row) => faDigits(Number(row.totalRows)) },
    { id: 'imported', headerKey: 'plt:import.history.columns.imported', cell: (row) => faDigits(Number(row.progress.imported)) },
    { id: 'created', headerKey: 'plt:import.history.columns.created', cell: (row) => <JalaliDate value={row.createdAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'plt:import.history.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="flex-start" data-testid={`import-history-row-${row.id}`}>
          <Tooltip title={t('plt:import.history.open')}>
            <IconButton aria-label={t('plt:import.history.open')} onClick={() => onOpen(row)} data-testid={`import-open-${row.id}`}>
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          {EXECUTED_STATUSES.includes(row.status) && Number(row.progress.skipped) > 0 && <ErrorsDownloadButton job={row} />}
          <RollbackAction job={row} />
          {!row.executedAt && !isBusy(row) && (
            <Tooltip title={t('plt:import.history.delete')}>
              <span>
                <IconButton
                  aria-label={t('plt:import.history.delete')}
                  disabled={!write.canWrite}
                  onClick={() => setDeleting(row)}
                  data-testid={`import-delete-${row.id}`}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <DataTable
        captionKey="plt:import.history.caption"
        columns={columns}
        rows={list.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        hasMore={list.hasNextPage}
        onLoadMore={() => void list.fetchNextPage()}
        isFetchingMore={list.isFetchingNextPage}
        emptyTitleKey="plt:import.history.empty"
        emptyAction={
          <Button onClick={() => void list.refetch()}>{t('common:actions.reload')}</Button>
        }
      />
      <ConfirmDialog
        open={deleting !== null}
        titleKey="plt:import.history.delete_title"
        messageKey="plt:import.history.delete_message"
        confirmKey="plt:import.history.delete"
        destructive
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
      />
    </>
  );
}
